import { FiltersControl } from '../Filters/types';
import { useWatchCommonDataFields } from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import * as React from 'react';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { useToggle } from 'react-use';
import { DatasetDescriptionData } from '../../../shared/api/api';
import { Row } from '@tanstack/react-table';

type CommonFilters = { region: string; delay: string | number; universe: string };

function getIsActiveRow({ field, selectedFilters }: { field?: CommonFilters; selectedFilters: CommonFilters }) {
  return (
    selectedFilters.region === field?.region &&
    Number(selectedFilters.delay) === field?.delay &&
    selectedFilters.universe === field?.universe
  );
}

export function useSingleEntityDataTable<DataType>(control: FiltersControl, data?: DataType[]) {
  const selectedFilters = useWatchCommonDataFields(control);

  const getTrProps = useCallback(
    (state: any, rowInfo?: Row<DataType>, column?: undefined) => {
      const isAggregatedRow = Number(rowInfo?.subRows?.length) > 1;
      const rowValues = rowInfo?.original as DatasetDescriptionData;

      const getIsActiveNonAggregatedRow = () => {
        return !isAggregatedRow && getIsActiveRow({ field: rowValues, selectedFilters });
      };

      const className = getIsActiveNonAggregatedRow() ? 'rt-tr--active' : '';

      return {
        className,
        onClick: (ev: React.MouseEvent<HTMLElement>) => {
          if (isAggregatedRow) {
            return;
          }

          control.setValue(filterFields.region.name, rowValues?.region);
          control.setValue(filterFields.universe.name, rowValues?.universe);
          control.setValue(filterFields.delay.name, String(rowValues?.delay));
        },
      };
    },
    [control, selectedFilters]
  );

  const [isTableExpanded, toggleTableExpanded] = useToggle(false);
  const [expandedRows, setExpandedRows] = useState({} as Record<string, boolean>);

  const activeFieldData = useMemo(
    () =>
      data?.find(field =>
        getIsActiveRow({
          field: field as CommonFilters,
          selectedFilters,
        })
      ),
    [data, selectedFilters]
  );
  const isAllDataShown = isTableExpanded || !activeFieldData;

  const onToggleTableExpanded = () => {
    if (isTableExpanded) {
      //   collapse all rows if we collapse table
      setExpandedRows({});
    }
    toggleTableExpanded();
  };

  // this effect controls default expanded rows depending on selected filters
  useLayoutEffect(() => {
    if (data && isTableExpanded) {
      if (!!activeFieldData) {
        setExpandedRows(prevExpanded => ({ ...prevExpanded, [`region:${selectedFilters.region}`]: true }));
      } else {
        setExpandedRows({});
      }
    }
  }, [data, selectedFilters, isTableExpanded, activeFieldData]);

  const dataToRender = useMemo(() => {
    if (isAllDataShown) {
      return data;
    }
    return [activeFieldData];
  }, [data, isAllDataShown, activeFieldData]);

  const isMoreDataPresent = data?.length ?? 0 > 1;

  const isExpandVisible = data && isMoreDataPresent && !!activeFieldData;
  return {
    getTrProps,
    isTableExpanded,
    expandedRows,
    setExpandedRows,
    activeFieldData,
    isAllDataShown,
    toggleTableExpanded: onToggleTableExpanded,
    dataToRender,
    isExpandVisible,
  };
}
